import React, { useEffect, useState, useCallback } from "react";
import { API } from "../context/AuthContext";

const AGENT_NAMES = {
  strategist:     "Estratega",
  onboarding:     "Onboarding",
  copywriter:     "Copywriter",
  contentPlanner: "Planificador",
  adsSpecialist:  "Ads",
  seoAnalyst:     "SEO",
  reportAnalyst:  "Reportes",
  upselling:      "Upselling",
  competitive:    "Competencia",
  qa:             "QA",
  sync:           "Sync de perfil",
};

function fmt(n) {
  return Number(n).toLocaleString("es-AR");
}
function fmtUSD(n) {
  return `$${Number(n).toFixed(4)}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Usage() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to,   setTo]   = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`/api/usage?from=${from}&to=${to}`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h1 className="page-title">Consumo de API</h1>
            <p className="page-subtitle">Tokens y costo en USD — Claude Sonnet 5</p>
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Date range selector */}
        <div style={{
          display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap",
          alignItems: "flex-end",
        }}>
          <div>
            <label className="label">Desde</label>
            <input type="date" className="input" value={from}
              onChange={e => setFrom(e.target.value)}
              style={{ width: 160 }} />
          </div>
          <div>
            <label className="label">Hasta</label>
            <input type="date" className="input" value={to}
              onChange={e => setTo(e.target.value)}
              style={{ width: 160 }} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(daysAgo(6));  setTo(today()); }}>7 días</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(daysAgo(29)); setTo(today()); }}>30 días</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(daysAgo(89)); setTo(today()); }}>90 días</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 64 }}><div className="spinner" /></div>
        ) : !data ? (
          <div className="empty-state"><h3>Error al cargar datos</h3></div>
        ) : (
          <>
            {/* Summary cards */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 16, marginBottom: 32,
            }}>
              <SummaryCard
                label="Tokens de entrada"
                value={fmt(data.totals.input_tokens)}
                sub="input tokens"
                color="var(--accent)"
              />
              <SummaryCard
                label="Tokens de salida"
                value={fmt(data.totals.output_tokens)}
                sub="output tokens"
                color="#8b5cf6"
              />
              <SummaryCard
                label="Total tokens"
                value={fmt(data.totals.total_tokens)}
                sub="combinados"
                color="var(--text-secondary)"
              />
              <SummaryCard
                label="Costo total"
                value={`$${data.totals.usd.toFixed(4)}`}
                sub="USD estimado"
                color="var(--success)"
                highlight
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>

              {/* By client */}
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>Por cliente</h3>
                </div>
                {data.by_client.length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                    Sin datos en este período
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--border)" }}>
                        {["Cliente", "Tokens", "USD"].map(h => (
                          <th key={h} style={{ padding: "8px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_client.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 500, color: "var(--text-primary)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.client_name}
                          </td>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--text-secondary)" }}>
                            <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{fmt(row.total_tokens)}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>↑{fmt(row.input_tokens)} ↓{fmt(row.output_tokens)}</div>
                          </td>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                            {fmtUSD(row.usd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* By agent */}
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>Por agente</h3>
                </div>
                {data.by_agent.length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                    Sin datos en este período
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--border)" }}>
                        {["Agente", "Tokens", "USD"].map(h => (
                          <th key={h} style={{ padding: "8px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_agent.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
                            {AGENT_NAMES[row.agent_id] || row.agent_id}
                          </td>
                          <td style={{ padding: "10px 16px", fontSize: 13 }}>
                            <div style={{ color: "var(--text-primary)" }}>{fmt(row.total_tokens)}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>↑{fmt(row.input_tokens)} ↓{fmt(row.output_tokens)}</div>
                          </td>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                            {fmtUSD(row.usd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Daily trend */}
            {data.daily.length > 0 && (
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>Consumo diario</h3>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--border)" }}>
                        {["Fecha", "Tokens entrada", "Tokens salida", "Total tokens", "USD"].map(h => (
                          <th key={h} style={{ padding: "8px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.daily.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                            {new Date(row.day).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })}
                          </td>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--text-secondary)" }}>{fmt(row.input_tokens)}</td>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--text-secondary)" }}>{fmt(row.output_tokens)}</td>
                          <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{fmt(row.total_tokens)}</td>
                          <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--success)", fontWeight: 500 }}>{fmtUSD(row.usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p style={{ marginTop: 16, fontSize: 11, color: "var(--text-muted)" }}>
              * Precios configurados por millón de tokens (entrada / salida) — claude-sonnet-5
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, color, highlight }) {
  return (
    <div className="card" style={{
      background: highlight ? "rgba(34,197,94,0.06)" : "var(--bg-surface)",
      border: highlight ? "1px solid rgba(34,197,94,0.2)" : "1px solid var(--border)",
    }}>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1, marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{sub}</div>
    </div>
  );
}
